import TransparentPricingC from '@src/quince-components/src/atoms/transparentPricing'
import { getDiscountedPrice } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import { TransparentPricingItem } from '@src/quince-site-common/src/domains/ProductTransparentPricing'
import { IProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import formatDisplayAmountValue from '@src/quince-site-common/src/utils/formatDisplayAmountValue'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import { getProductPriceMap } from '../bundleProductPurchase/BundleProductPurchase.helpers'

import { pidAndSockDonationCostMap, shouldHideTransparentPricing } from './TransparentPricing'
import styles from './transparentPricing.module.scss'

const TransparentPricingBundle = (props: BundlePageSectionProps) => {
  const { t } = useTranslation('common')
  const discount = props.bundle.discountPrice ?? 0

  const bundleSelectedOptions = useAppSelector<{ product: Product; selectedOptions: IProductVariantOption[] }[]>(
    pdpStateManager,
    pdpStateManager.getBundleSelectedOptions,
  )

  const prices = getProductPriceMap(bundleSelectedOptions)
  const productsSumPrice = formatDisplayAmountValue(
    prices.reduce((sum, { product: _, pricing }) => sum + parseFloat(pricing.formattedSalePrice), 0),
  )
  const quincePrice = getDiscountedPrice(Number(productsSumPrice), discount)

  const traditionalPrice = formatDisplayAmountValue(
    prices.reduce((sum, { product: _, pricing }) => sum + parseFloat(pricing.formattedTraditionalPrice ?? '0'), 0),
  )

  const costsForVariant: TransparentPricingItem = useMemo(() => {
    const _costsForVariant = {
      crafting: 0,
      creditCardFees: 0,
      hardware: 0,
      lastBrandFindersFee: 0,
      materials: 0,
      name: '',
      packaging: 0,
      shippingHandling: 0,
      shopifyVariantId: 0,
      variantId: 0,
      totalPrice: 0,
      sockDonationCost: 0,
    }
    bundleSelectedOptions.forEach(({ product, selectedOptions }) => {
      const transparentPricing = productHelper.getTransparentPricingCostsForOptions(
        product,
        selectedOptions,
        pidAndSockDonationCostMap,
      )
      _costsForVariant.crafting += transparentPricing?.crafting ?? 0
      _costsForVariant.creditCardFees += transparentPricing?.creditCardFees ?? 0
      _costsForVariant.hardware += transparentPricing?.hardware ?? 0
      _costsForVariant.lastBrandFindersFee += transparentPricing?.lastBrandFindersFee ?? 0
      _costsForVariant.materials += transparentPricing?.materials ?? 0
      _costsForVariant.packaging += transparentPricing?.packaging ?? 0
      _costsForVariant.shippingHandling += transparentPricing?.shippingHandling ?? 0
      _costsForVariant.totalPrice += transparentPricing?.totalPrice ?? 0
      _costsForVariant.sockDonationCost += transparentPricing?.sockDonationCost ?? 0
    })
    return _costsForVariant
  }, [bundleSelectedOptions])

  const getTransparentPricingItems = () => {
    const transparentPricingItems = [
      {
        name: t('materials'),
        value: (costsForVariant.materials || 0) + (costsForVariant.hardware || 0),
      },
      {
        name: t('crafting_cost'),
        value: costsForVariant.crafting || 0,
      },
      {
        name: t('packaging'),
        value: costsForVariant.packaging || 0,
      },
      {
        name: t('freight_handling'),
        value: costsForVariant.shippingHandling || 0,
      },
      {
        name: t('credit_card_fees'),
        value: costsForVariant.creditCardFees || 0,
      },
    ]
    if (costsForVariant.dutyFee) {
      transparentPricingItems.push({
        name: t('duties_taxes_and_fees'),
        value: costsForVariant.dutyFee || 0,
      })
    }
    if (costsForVariant.sockDonationCost) {
      return [
        ...transparentPricingItems,
        {
          name: t('sock_donation'),
          value: costsForVariant.sockDonationCost,
        },
      ]
    }
    return transparentPricingItems
  }

  if (shouldHideTransparentPricing(props.bundle)) return null
  return (
    <div className={styles.transparentPricing}>
      <TransparentPricingC
        items={getTransparentPricingItems()}
        quincePrice={+quincePrice}
        traditionalPrice={+traditionalPrice || 0}
      />
    </div>
  )
}

export default TransparentPricingBundle
